import type { SlateBlock, BulletedListBlock, ListItemBlock, TableCellBlock, TableRowBlock } from '@atlas/renderer';
import extractBlockFromAtlasElement from '@atlas/tool/box/slate/extractBlockFromAtlasElement';
import extractListBlockFromListElement from '@atlas/tool/box/slate/extractListBlockFromListElement';
import extractNodeListPlainText from '@atlas/tool/box/slate/extractNodeListPlainText';
import extractNodeListText from '@atlas/tool/box/slate/extractNodeListText';
import extractNodeTextRecursively from '@atlas/tool/box/slate/extractNodeTextRecursively';
import extractVideoEmbedBlockFromIframeElement from '@atlas/tool/box/slate/extractVideoEmbedBlockFromIframeElement';
import isHtmlElementNode from '@atlas/tool/box/dom/isHtmlElementNode';
import isTextNode from '@atlas/tool/box/dom/isTextNode';
import extractVideoNodeSources from '@atlas/tool/box/slate/extractVideoNodeSources';
import isCommentNode from '@atlas/tool/box/dom/isCommentNode';
import isListHtmlElement from '@atlas/tool/box/dom/isListHtmlElement';
import type { TTextHtmlElement } from '@atlas/tool/box/dom/isTextHtmlElement';

export default function extractBlocksFromHtml(html: string): SlateBlock[] {
    if (!html.trim()) return [];

    const bodyElement = new DOMParser().parseFromString(
        // <table> wrapper is needed to handle td/th/tr/tbody/thead/tfoot without wrapper tag
        // It creates empty table tag that is safe for parser
        `<table class="atlas-block atlas-block-table">${html}</table>`,
        'text/html',
    ).body;

    return extractBlocksFromHtmlElement(bodyElement);
}

// #region Helpers

const textElementsBlockMap: Record<TTextHtmlElement['tagName'], SupportedTextBlockName> = {
    P: 'paragraph',
    DIV: 'paragraph',
    H1: 'heading-one',
    H2: 'heading-two',
    H3: 'heading-three',
    H4: 'paragraph',
    H5: 'paragraph',
    H6: 'paragraph',
    BLOCKQUOTE: 'block-quote',
    PRE: 'code-block',
};

function extractBlocksFromHtmlElement(node: HTMLElement): SlateBlock[] {
    const atlasBlock = extractBlockFromAtlasElement(node);
    if (atlasBlock) {
        node.remove();
        return [atlasBlock];
    }

    if (
        isCommentNode(node) ||
        isHtmlElementNode(node, 'STYLE') ||
        isHtmlElementNode(node, 'SCRIPT') ||
        isHtmlElementNode(node, 'BR')
    ) {
        node.remove();
        return [];
    }

    if (isHtmlElementNode(node, 'HR')) {
        node.remove();
        return [{ type: 'horizontal-line', children: [{ text: '' }] }];
    }

    if (isHtmlElementNode(node, 'IMG')) {
        if (!node.src) return [];
        node.remove();
        return [
            {
                type: 'image',
                src: node.src,
                alt: node.alt,
                width: parseInt(node.getAttribute('width') || '') || undefined,
                children: [{ text: '' }],
            },
        ];
    }

    if (isHtmlElementNode(node, 'VIDEO')) {
        const sources = extractVideoNodeSources(node);
        node.remove();
        if (!sources) return [];
        return [{ type: 'video', sources, alt: node.title, children: [{ text: '' }] }];
    }

    if (isHtmlElementNode(node, 'IFRAME')) {
        const videoEmbedBlock = extractVideoEmbedBlockFromIframeElement(node);
        node.remove();
        if (videoEmbedBlock) return [videoEmbedBlock];

        const source = node.src;
        if (!source) return [];

        const height = (node.height && parseInt(node.height)) || 400;

        return [{ type: 'embed', source, height, children: [{ text: '' }] }];
    }

    if (isHtmlElementNode(node, 'BLOCKQUOTE')) {
        const children = extractNodeListPlainText(node.childNodes);
        node.remove();
        return [{ type: 'block-quote', children }];
    }

    if (isHtmlElementNode(node, 'PRE')) {
        const children = extractNodeListPlainText(node.childNodes);
        node.remove();
        return [{ type: 'code-block', children }];
    }

    if (isHtmlElementNode(node, 'TABLE')) {
        node.remove();

        if (!node.rows.length) return [];

        const columnsCount = Math.max(...Array.from(node.rows).map((row) => row.cells.length));
        if (!columnsCount) return [];

        const rows: TableRowBlock[] = Array.from(node.rows).map<TableRowBlock>((row) => {
            const cells: TableCellBlock[] = Array.from(row.cells).map((cell) => {
                const content = extractNodeTextRecursively(cell.childNodes);
                return {
                    type: 'table-cell',
                    children: content.length ? content : [{ text: '' }],
                };
            });

            if (cells.length < columnsCount) {
                cells.push(
                    ...new Array(columnsCount - cells.length).fill({
                        type: 'table-cell',
                        children: [{ text: '' }],
                    }),
                );
            }

            return { type: 'table-row', children: cells };
        });

        const colgroup = Array.from(node.children).find((el) => el.tagName === 'COLGROUP');

        const columns = colgroup?.children.length
            ? Array.from(colgroup.children).map((col) => ({
                  width: parseInt((col as HTMLTableColElement).width) || 0,
              }))
            : undefined;

        return [{ type: 'table', children: rows, columns }];
    }

    // Flat content blocks

    if (!node.childNodes.length) {
        node.remove();
        return [];
    }

    const children = isHtmlElementNode(node, 'PRE')
        ? extractNodeListPlainText(node.childNodes)
        : extractNodeListText(node.childNodes, isHtmlElementNode(node, 'BLOCKQUOTE'));

    const type: SlateBlock['type'] =
        node.tagName in textElementsBlockMap
            ? textElementsBlockMap[node.tagName as TTextHtmlElement['tagName']]
            : 'paragraph';
    const blocks: SlateBlock[] = children.length ? [{ type, children }] : [];

    if (!node.childNodes.length) {
        node.remove();
        return blocks;
    }

    // Nested blocks cases

    const firstChildNode = node.childNodes[0];

    if (!(firstChildNode instanceof HTMLElement)) {
        firstChildNode.remove();
        return [...blocks, ...extractBlocksFromHtmlElement(node)];
    }

    if (isListHtmlElement(firstChildNode)) {
        // TODO: @Stan handle insertion of lists from Dropbox Paper

        if (!firstChildNode.querySelector('.atlas-block')) {
            const listBlock = extractListBlockFromListElement(firstChildNode);
            firstChildNode.remove();
            if (listBlock) return [listBlock, ...extractBlocksFromHtmlElement(node)];
            return extractBlocksFromHtmlElement(node);
        }

        const children = Array.from(node.children);
        const listChildrenCount = children.findIndex(
            (child) => !isListHtmlElement(child) && !child.classList.contains('atlas-block-list'),
        );

        const wrapperBlockElement = Object.assign(document.createElement('div'), {
            className: `atlas-block atlas-block-list atlas-block-list-${
                isHtmlElementNode(firstChildNode, 'OL') ? 'ordered' : 'unordered'
            }`,
        });

        children.slice(0, listChildrenCount + 1).forEach((listElement) => {
            wrapperBlockElement.appendChild(listElement);
        });

        const listBlocks = extractBlocksFromHtmlElement(wrapperBlockElement);
        return listBlocks;
    }

    if (isHtmlElementNode(firstChildNode, 'LI')) {
        const children: ListItemBlock[] = [];

        while (node.childNodes.length) {
            const item = node.childNodes[0];
            if (isTextNode(item)) {
                if (item.textContent) {
                    if (!children.length) {
                        children.push({ type: 'list-item', children: [] });
                    }
                    children[children.length - 1].children.push({
                        text: item.textContent,
                    });
                }
                item.remove();
                continue;
            }

            if (!isHtmlElementNode(item)) {
                item.remove();
                continue;
            }

            if (isHtmlElementNode(item, 'LI')) {
                const content = extractNodeTextRecursively(item.childNodes);
                item.remove();

                if (content.length) {
                    children.push({ type: 'list-item', children: content });
                }

                continue;
            }

            break;
        }

        if (!children.length) {
            return [...blocks, ...extractBlocksFromHtmlElement(node)];
        }

        const listBlock: BulletedListBlock = {
            type: 'bulleted-list',
            children,
        };

        return [...blocks, listBlock, ...extractBlocksFromHtmlElement(node)];
    }

    return [...blocks, ...extractBlocksFromHtmlElement(firstChildNode), ...extractBlocksFromHtmlElement(node)];
}

// #endregion

// #region Types

type SupportedTextBlockName =
    | 'paragraph'
    | 'heading-one'
    | 'heading-two'
    | 'heading-three'
    | 'block-quote'
    | 'code-block';

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('extractBlocksFromHtml', async () => {
        describe('lists', () => {
            it('extracts properly structured element', () => {
                const html = '<ol><li>hello</li></ol>';
                expect(extractBlocksFromHtml(html)).to.deep.equal([
                    {
                        type: 'numbered-list',
                        children: [{ type: 'list-item', children: [{ text: 'hello' }] }],
                    },
                ]);
            });

            it('extracts from paragraph', () => {
                const html =
                    '<p>Hey, this is Rahul, cofounder of Atlas. Great to see you here!</p> <p>You can edit the ticket properties at the top of the ticket here in the timeline.</p> <p> Here are a few things you can do with tickets:\n  <ul>\n    <li><b>assign</b> this ticket to a teammate by hovering over the ticket and pressing "A".</li>\n    <li><b>prioritize</b> the ticket by hovering over the ticket and pressing "P".</li>\n    <li><b>close</b> the ticket by hovering over the ticket and pressing "C".</li>\n  </ul>\n</p>';
                expect(extractBlocksFromHtml(html)).to.deep.equal([
                    {
                        type: 'paragraph',
                        children: [{ text: 'Hey, this is Rahul, cofounder of Atlas. Great to see you here!' }],
                    },
                    {
                        type: 'paragraph',
                        children: [
                            {
                                text: 'You can edit the ticket properties at the top of the ticket here in the timeline.',
                            },
                        ],
                    },
                    { type: 'paragraph', children: [{ text: ' Here are a few things you can do with tickets:\n  ' }] },
                    {
                        type: 'bulleted-list',
                        children: [
                            {
                                type: 'list-item',
                                children: [
                                    { text: 'assign', bold: true },
                                    {
                                        text: ' this ticket to a teammate by hovering over the ticket and pressing "A".',
                                    },
                                ],
                            },
                            {
                                type: 'list-item',
                                children: [
                                    { text: 'prioritize', bold: true },
                                    { text: ' the ticket by hovering over the ticket and pressing "P".' },
                                ],
                            },
                            {
                                type: 'list-item',
                                children: [
                                    { text: 'close', bold: true },
                                    { text: ' the ticket by hovering over the ticket and pressing "C".' },
                                ],
                            },
                        ],
                    },
                ]);
            });
        });

        it('extracts inline formatting elements with spaces in between', () => {
            const html =
                '<p><strong>More</strong> <em>formatted</em> <span style="text-decoration: line-through">text</span> with <a href="https://atlas.so">link</a></p>';
            expect(extractBlocksFromHtml(html)).to.deep.equal([
                {
                    type: 'paragraph',
                    children: [
                        { text: 'More', bold: true },
                        { text: ' ' },
                        { text: 'formatted', italic: true },
                        { text: ' ' },
                        { text: 'text', strikethrough: true },
                        { text: ' with ' },
                        { text: 'link', link: 'https://atlas.so/' },
                    ],
                },
            ]);
        });
    });
}

// #endregion
